// Inventory area shell (office, Module 06 §4). Renders the sub-navigation and
// an <Outlet/> for the §4.1–§4.10 screens. Registered at /inventory; the index
// redirects to /inventory/lookup — §4.1 is "the default landing screen for the
// storekeeper role", and it is the right landing screen for everyone else too
// (it is the only screen that answers a question without an id in hand).
//
// Nav gating mirrors §7 (fail-closed — see hooks/permissions.ts): the count
// screen is offered to everyone who has business there and the trace launcher
// only to `inventory.trace.read` holders (floor_worker gets 403 on trace
// endpoints, so a link would be a dead end). Everything else rides
// `inventory.read`, which every internal role holds.
//
// THE COUNTS ENTRY IS NOT A REVIEWER-ONLY DOOR (surface review F1, 2026-08-12).
// It used to be gated on `inventory.count.review` alone, while the screen
// behind it renders its one write — "New count" — for `inventory.count.perform`
// holders. §7 grants the storekeeper `count.perform` and
// `count_schedule.manage` and deliberately NOT `count.review`, so the role that
// starts counts had no way in: the screen's only mutation belonged to a role
// the navigation refused. The entry is offered to any of the three count codes
// and the screen states what each caller may do (counts-list.tsx).

// UX-3: the sub-navigation is the §3.10 `RouterTabs` primitive — URL-backed
// links with `aria-current`, underlined in the accent, wrapping (never
// scrolling) at ≤768 px, which is what the M06 responsive net requires of
// navigation at 412 px. It used to be eight bare hyperlinks borrowing the
// CATALOGUE's `.catalogue-subnav-link` class: ad-hoc tab markup (gate check
// 3a) and a cross-area style dependency in one.
//
// RENDER-ONCE (§5), THE WHOLE AREA AT ONCE — the catalogue's rule (UX-2) and
// production's, for the same reason: two of the eight entries are
// permission-decided, and at 412 px this strip WRAPS, so an entry resolving
// late does not nudge the page, it adds a row to it. The area holds a
// reserving skeleton until the one cached role query settles and then remounts
// atomically, so nothing painted early ever moves.

import { Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { useTerm } from "../../onboarding/hooks/terminology";
import { useInventoryPermissions } from "./hooks/permissions";
import "./inventory.css";

export function InventoryLayout() {
  const { t } = useTranslation();
  const { term } = useTerm();
  const perms = useInventoryPermissions();

  const tabs: RouterTabDef[] = [
    { to: "/inventory/lookup", label: t("inventory.nav.lookup") },
    { to: "/inventory/locations", label: term("location") },
    { to: "/inventory/transfers", label: term("transfer") },
    ...(perms.has("inventory.count.review") ||
    perms.has("inventory.count.perform") ||
    perms.has("inventory.count_schedule.manage")
      ? [{ to: "/inventory/counts", label: term("cycle_count") }]
      : []),
    // §4.11 — offered to the roles that can act on a held adjustment (decide)
    // or withdraw their own (cancel); everyone else reaches it only through
    // the link on an escalated count.
    ...(perms.has("inventory.count.review") || perms.has("inventory.adjust")
      ? [{ to: "/inventory/approvals", label: t("inventory.nav.approvals") }]
      : []),
    { to: "/inventory/movements", label: t("inventory.nav.movements") },
    { to: "/inventory/atp", label: t("inventory.nav.atp") },
    { to: "/inventory/uom-conversions", label: t("inventory.nav.uomConversions") },
  ];

  if (!perms.isResolved) {
    return (
      <div className="inventory-area" key="inv-loading" data-testid="inventory-loading">
        <div className="ds-tabs inv-subnav-skel" aria-hidden="true" />
        <div className="inventory-body">
          <CardSkeleton lines={6} label={t("inventory.common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-area" key="inv-ready">
      <RouterTabs tabs={tabs} label={t("inventory.nav.aria")} data-testid="inventory-subnav" />
      <div className="inventory-body">
        <Outlet />
      </div>
    </div>
  );
}
